import React, { useState, useContext, useEffect } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { canUserDelete } from '@/utils/permissions';
import ListLoadError from '@/components/ListLoadError';
import { 
  Plus, Search, Filter, Download, Edit2, Trash2, Eye, X, Save,
  AlertTriangle, CheckCircle, Clock, FileText, Package, DollarSign,
  Calendar, User, Building2, ChevronDown, Loader2, RefreshCw, Scale, Upload
} from 'lucide-react';

// Legacy Quality claims stored their attachment breakdown only inside the
// `description` string. This helper extracts numeric values so the modal and
// PDF can render them even when structured fields are empty.
function parseQualityDescription(description) {
  if (!description || typeof description !== 'string') return {};
  const pick = (re) => {
    const m = description.match(re);
    return m ? parseFloat(m[1].replace(/,/g, '')) : null;
  };
  const out = {};
  const contract = pick(/as per contract[^\d%-]*(?:attachment)?\s*%?\s*[-:]\s*(-?\d+(?:\.\d+)?)/i);
  const actual = pick(/actual\s*attachment\s*%?\s*[-:]\s*(-?\d+(?:\.\d+)?)/i);
  const claimable = pick(/claimable\s*attachment\s*%?\s*[-:]\s*(-?\d+(?:\.\d+)?)/i);
  const invQty = pick(/invoice\s*quantity\s*\(?\s*mt\s*\)?\s*[-:]\s*(-?\d+(?:\.\d+)?)/i);
  const rate = pick(/rate\s*(?:in\s*usd)?\s*[-:]\s*(-?\d+(?:\.\d+)?)/i);
  const claimQty = pick(/claim\s*quantity\s*(?:in\s*mt)?\s*[-:]\s*(-?\d+(?:\.\d+)?)/i);
  const claimAmt = pick(/(?:attachment\s*)?claim\s*amount(?:\s*in\s*usd)?\s*[-:]\s*\$?\s*(-?\d+(?:[,.]\d+)*(?:\.\d+)?)/i);
  if (contract !== null) out.contractAttachment = contract;
  if (actual !== null) out.actualAttachment = actual;
  if (claimable !== null) out.claimableAttachment = Math.abs(claimable);
  if (invQty !== null) out.qualityReceivedQty = invQty;
  if (rate !== null) out.qualityRate = rate;
  if (claimQty !== null) out.claimQtyMt = claimQty;
  if (claimAmt !== null) out.claimAmountParsed = claimAmt;
  return out;
}

function claimWithFallback(claim) {
  if (!claim || claim.claimType !== 'Quality Issue') return claim;
  // If any quality field already has a non-zero value, trust the stored data.
  const hasStructured = ['contractAttachment','actualAttachment','claimableAttachment','qualityRate','qualityReceivedQty']
    .some(k => parseFloat(claim[k] || 0) > 0);
  if (hasStructured) return claim;
  const parsed = parseQualityDescription(claim.description);
  const base = claim.qualityReceivedQty || parsed.qualityReceivedQty || 0;
  const rate = claim.qualityRate || parsed.qualityRate || 0;
  const claimable = claim.claimableAttachment || parsed.claimableAttachment || 0;
  const perMt = parseFloat(claim.claimAmountPerMT || 0) ||
                (rate && claimable ? (rate * claimable / 100) : 0);
  return {
    ...claim,
    contractAttachment: claim.contractAttachment || parsed.contractAttachment || 0,
    actualAttachment: claim.actualAttachment || parsed.actualAttachment || 0,
    claimableAttachment: claimable,
    qualityReceivedQty: base,
    qualityRate: rate,
    claimAmountPerMT: perMt,
  };
}

function Claims() {
  const { API, user } = useContext(AppContext);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claimsLoadError, setClaimsLoadError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');
  const [saving, setSaving] = useState(false);
  
  // Check if user can delete (Admin or 'full' access)
  const canDelete = canUserDelete(user, '/imports/claims');
  const [message, setMessage] = useState(null);
  
  // GRN list for linking claims
  const [grnList, setGrnList] = useState([]);
  
  const [formData, setFormData] = useState({
    claimNumber: '',
    grnNumber: '',
    invoiceNo: '',
    partyName: '',
    containerNo: '',
    claimType: 'Shortage',
    claimDate: new Date().toISOString().split('T')[0],
    invoiceWeight: '',
    actualWeight: '',
    shortageQty: '',
    claimAmount: '',
    currency: 'USD',
    description: '',
    status: 'Pending',
    documents: [],
    remarks: '',
    // Quality Issue fields
    contractAttachment: '',
    actualAttachment: '',
    claimableAttachment: '',
    qualityRate: '',
    qualityReceivedQty: '',
    claimAmountPerMT: '',
    totalClaim: ''
  });

  const claimTypes = ['Weighment Claim', 'Shortage', 'Damage', 'Quality Issue', 'Wrong Material', 'Delayed Delivery', 'Other'];
  const claimStatuses = ['Pending', 'Under Review', 'Approved', 'Rejected', 'Settled', 'Closed'];

  const [syncing, setSyncing] = useState(false);
  
  // Excel upload state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  useEffect(() => {
    fetchGRNList();
    // Sync shortages in background — don't block GRN dropdown loading
    syncGRNShortages(true);
  }, []);

  const syncGRNShortages = async (silent = false) => {
    if (!silent) setSyncing(true);
    try {
      const response = await axios.post(`${API}/imports/claims/sync-grn-shortages`);
      const data = response.data;
      const createdCount = data.created?.length || data.createdClaims?.length || 0;
      const updatedCount = data.updated?.length || data.updatedClaims?.length || 0;
      
      if (!silent && (createdCount > 0 || updatedCount > 0)) {
        const parts = [];
        if (createdCount > 0) parts.push(`${createdCount} new claims created`);
        if (updatedCount > 0) parts.push(`${updatedCount} claims corrected`);
        setMessage({ 
          type: 'success', 
          text: `Sync complete: ${parts.join(', ')}` 
        });
      } else if (!silent) {
        setMessage({
          type: 'info',
          text: `Sync complete: ${data.message || 'All claims are up to date'}`
        });
      }
      // Fetch claims after sync
      await fetchClaims();
    } catch (error) {
      console.error('Error syncing GRN shortages:', error);
      if (!silent) {
        setMessage({ type: 'error', text: 'Failed to sync GRN shortages' });
      }
      // Still fetch claims even if sync fails
      await fetchClaims();
    } finally {
      if (!silent) setSyncing(false);
    }
  };

  const fetchClaims = async () => {
    setLoading(true);
    setClaimsLoadError(null);
    try {
      const response = await axios.get(`${API}/imports/claims`);
      setClaims(response.data || []);
    } catch (error) {
      console.error('Error fetching claims:', error);
      if (error?.response?.status !== 401) setClaimsLoadError(error);
    } finally {
      setLoading(false);
    }
  };

  const [grnLoadError, setGrnLoadError] = useState(false);

  const fetchGRNList = async () => {
    try {
      setGrnLoadError(false);
      const response = await axios.get(`${API}/raw-material/grn/list-brief`);
      const data = Array.isArray(response.data) ? response.data : [];
      setGrnList(data);
      if (data.length === 0) {
        console.warn('GRN list is empty');
      }
    } catch (error) {
      console.error('Error fetching GRN list:', error);
      setGrnLoadError(true);
      setGrnList([]);
    }
  };

  const generateClaimNumber = () => {
    const date = new Date();
    const year = date.getFullYear().toString().slice(-2);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `CLM/${year}${month}/${random}`;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      if (updated.claimType === 'Quality Issue') {
        // Quality Issue: auto-calculate claimable attachment, claim per MT, total claim
        if (['contractAttachment', 'actualAttachment', 'qualityRate', 'qualityReceivedQty'].includes(field) || field === 'claimType') {
          const contract = parseFloat(updated.contractAttachment) || 0;
          const actual = parseFloat(updated.actualAttachment) || 0;
          const rate = parseFloat(updated.qualityRate) || 0;
          const qty = parseFloat(updated.qualityReceivedQty) || 0;
          const claimable = Math.max(0, Math.round((actual - contract) * 10000) / 10000);
          const perMT = Math.round(rate * (claimable / 100) * 100) / 100;
          const total = Math.round(perMT * qty * 100) / 100;
          updated.claimableAttachment = claimable;
          updated.claimAmountPerMT = perMT;
          updated.totalClaim = total;
          updated.claimAmount = total;
        }
      } else {
        // Shortage/other: auto-calculate shortage quantity
        if (field === 'invoiceWeight' || field === 'actualWeight') {
          const invoice = parseFloat(updated.invoiceWeight) || 0;
          const actual = parseFloat(updated.actualWeight) || 0;
          if (invoice > 0 && actual > 0) {
            updated.shortageQty = (invoice - actual).toFixed(3);
          }
        }
      }
      
      return updated;
    });
  };

  const handleGRNSelect = (grnNumber) => {
    const grn = grnList.find(g => g.grnNumber === grnNumber);
    if (grn) {
      if (selectedClaim) {
        // Editing: only update the GRN link, preserve user's existing claim data
        setFormData(prev => ({
          ...prev,
          grnNumber: grn.grnNumber
        }));
      } else {
        // New claim: auto-fill all fields from GRN
        // For claim purposes: use CFS weight if available, else net/received weight
        const claimWeight = grn.cfsWeight ? parseFloat(grn.cfsWeight) : parseFloat(grn.netWeight) || 0;
        const invoiceWt = parseFloat(grn.invoiceWeight) || 0;
        const netWt = parseFloat(grn.netWeight) || 0;
        const grnRate = parseFloat(grn.rate) || 0;
        setFormData(prev => ({
          ...prev,
          grnNumber: grn.grnNumber,
          invoiceNo: grn.invoiceNo || '',
          partyName: grn.partyName || '',
          invoiceWeight: grn.invoiceWeight || '',
          actualWeight: claimWeight || '',
          containerNo: grn.containerNo || '',
          shortageQty: invoiceWt && claimWeight
            ? (invoiceWt - claimWeight).toFixed(3) 
            : '',
          currency: grn.currency || 'USD',
          // Quality issue fields from GRN
          qualityRate: grnRate || '',
          qualityReceivedQty: netWt || ''
        }));
      }
    } else if (grnNumber === '') {
      // Clearing the GRN link
      setFormData(prev => ({ ...prev, grnNumber: '' }));
    }
  };

  const handleSubmit = async () => {
    if (!formData.partyName || !formData.claimType) {
      setMessage({ type: 'error', text: 'Please fill required fields' });
      return;
    }

    setSaving(true);
    try {
      const claimData = {
        ...formData,
        claimNumber: formData.claimNumber || generateClaimNumber(),
        shortageQty: parseFloat(formData.shortageQty) || 0,
        claimAmount: parseFloat(formData.claimAmount) || 0,
        invoiceWeight: parseFloat(formData.invoiceWeight) || 0,
        actualWeight: parseFloat(formData.actualWeight) || 0,
        // Quality Issue fields
        contractAttachment: parseFloat(formData.contractAttachment) || 0,
        actualAttachment: parseFloat(formData.actualAttachment) || 0,
        claimableAttachment: parseFloat(formData.claimableAttachment) || 0,
        qualityRate: parseFloat(formData.qualityRate) || 0,
        qualityReceivedQty: parseFloat(formData.qualityReceivedQty) || 0,
        claimAmountPerMT: parseFloat(formData.claimAmountPerMT) || 0,
        totalClaim: parseFloat(formData.totalClaim) || 0,
        createdBy: user?.name || 'Admin',
        createdAt: new Date().toISOString()
      };

      if (selectedClaim) {
        await axios.put(`${API}/imports/claims/update`, claimData);
        setMessage({ type: 'success', text: 'Claim updated successfully!' });
      } else {
        await axios.post(`${API}/imports/claims`, claimData);
        setMessage({ type: 'success', text: 'Claim created successfully!' });
      }

      setShowModal(false);
      resetForm();
      fetchClaims();
    } catch (error) {
      console.error('Error saving claim:', error);
      setMessage({ type: 'error', text: 'Failed to save claim' });
    } finally {
      setSaving(false);
    }
    
    setTimeout(() => setMessage(null), 5000);
  };

  const handleDelete = async (claimNumber) => {
    if (!window.confirm('Are you sure you want to delete this claim?')) return;
    
    try {
      await axios.delete(`${API}/imports/claims/remove`, { data: { claimNumber } });
      setMessage({ type: 'success', text: 'Claim deleted successfully!' });
      fetchClaims();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete claim' });
    }
    
    setTimeout(() => setMessage(null), 5000);
  };

  // Excel upload handler
  const handleExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    setUploadResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/imports/claims/upload-excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadResult(response.data);
      setMessage({ type: 'success', text: `${response.data.created} claims created from Excel!` });
      fetchClaims();
    } catch (error) {
      const errMsg = error.response?.data?.detail || 'Failed to upload Excel';
      setUploadResult({ error: errMsg });
      setMessage({ type: 'error', text: errMsg });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
    
    setTimeout(() => setMessage(null), 5000);
  };

  // Download uploaded claims PDF
  const handleDownloadUploadPDF = async (pdfId) => {
    try {
      const response = await axios.get(`${API}/imports/claims/download-pdf/${pdfId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Claims_Upload.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setMessage({ type: 'error', text: 'Failed to download PDF' });
    }
  };

  // Download claim PDF
  const handleDownloadPDF = async (claimNumber) => {
    try {
      // Use the claim number directly in the URL path (not URL-encoded for slashes)
      const response = await axios.get(`${API}/imports/claims/pdf`, {
        params: { claim_number: claimNumber },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Claim_${claimNumber.replace(/\//g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setMessage({ type: 'success', text: 'PDF downloaded successfully!' });
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setMessage({ type: 'error', text: 'Failed to download PDF' });
    }
    
    setTimeout(() => setMessage(null), 5000);
  };

  const handleStatusChange = async (claimNumber, newStatus) => {
    try {
      await axios.put(`${API}/imports/claims/update-status`, { claimNumber, status: newStatus });
      setMessage({ type: 'success', text: `Status updated to ${newStatus}` });
      fetchClaims();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update status' });
    }
    
    setTimeout(() => setMessage(null), 5000);
  };

  const resetForm = () => {
    setFormData({
      claimNumber: '',
      grnNumber: '',
      invoiceNo: '',
      partyName: '',
      containerNo: '',
      claimType: 'Shortage',
      claimDate: new Date().toISOString().split('T')[0],
      invoiceWeight: '',
      actualWeight: '',
      shortageQty: '',
      claimAmount: '',
      currency: 'USD',
      description: '',
      status: 'Pending',
      documents: [],
      remarks: ''
    });
    setSelectedClaim(null);
  };

  const openEditModal = (claim) => {
    setSelectedClaim(claim);
    setFormData({
      ...claim,
      claimDate: claim.claimDate || new Date().toISOString().split('T')[0]
    });
    setShowModal(true);
    // Re-fetch GRN list when modal opens (in case initial load failed)
    if (grnList.length === 0) {
      fetchGRNList();
    }
  };

  const openViewModal = (claim) => {
    setSelectedClaim(claim);
    setShowViewModal(true);
  };

  const filteredClaims = claims.filter(claim => {
    const matchesSearch = 
      claim.claimNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      claim.partyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      claim.invoiceNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      claim.grnNumber?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'All' || claim.status === statusFilter;
    const matchesType = typeFilter === 'All' || claim.claimType === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending': return 'bg-yellow-100 text-yellow-700';
      case 'Under Review': return 'bg-blue-100 text-blue-700';
      case 'Approved': return 'bg-green-100 text-green-700';
      case 'Rejected': return 'bg-red-100 text-red-700';
      case 'Settled': return 'bg-purple-100 text-purple-700';
      case 'Closed': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Weighment Claim': return <Scale className="text-blue-600" size={16} />;
      case 'Shortage': return <Package className="text-orange-500" size={16} />;
      case 'Damage': return <AlertTriangle className="text-red-500" size={16} />;
      case 'Quality Issue': return <AlertTriangle className="text-yellow-500" size={16} />;
      default: return <FileText className="text-blue-500" size={16} />;
    }
  };

  // Calculate summary stats
  const stats = {
    total: claims.length,
    pending: claims.filter(c => c.status === 'Pending').length,
    approved: claims.filter(c => c.status === 'Approved').length,
    totalAmountUSD: claims.filter(c => c.currency === 'USD').reduce((sum, c) => sum + (parseFloat(c.claimAmount) || 0), 0),
    totalAmountINR: claims.filter(c => c.currency !== 'USD').reduce((sum, c) => sum + (parseFloat(c.claimAmount) || 0), 0),
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-orange-100 rounded-xl">
            <AlertTriangle className="text-orange-600" size={28} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Import Claims</h1>
            <p className="text-sm text-gray-500">Track and manage claims for imported materials</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={async () => {
              try {
                setMessage({ type: 'success', text: 'Fixing GRN links using invoice matching...' });
                const res = await axios.post(`${API}/imports/claims/fix-grn-links`);
                const fixed = res.data.fixed || 0;
                const alreadyCorrect = res.data.alreadyCorrect || 0;
                const unmatched = res.data.unmatched || 0;
                setMessage({ 
                  type: fixed > 0 ? 'success' : 'success', 
                  text: `GRN link fix complete: ${fixed} fixed, ${alreadyCorrect} already correct, ${unmatched} unmatched. Refreshing...` 
                });
                fetchClaims();
              } catch (err) {
                setMessage({ type: 'error', text: 'Failed to fix GRN links' });
              }
            }}
            className="flex items-center gap-2 px-4 py-2.5 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors font-medium text-sm"
            data-testid="fix-grn-links-btn"
          >
            <RefreshCw size={16} />
            Fix GRN Links
          </button>
          <button
            onClick={() => window.open(`${API}/imports/claims/audit-report`, '_blank')}
            className="flex items-center gap-2 px-4 py-2.5 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 transition-colors font-medium text-sm"
            data-testid="audit-report-btn"
          >
            <Download size={16} />
            Audit Report
          </button>
          <button
            onClick={() => setShowUploadModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-teal-500 text-white rounded-xl hover:bg-teal-600 transition-colors font-medium text-sm"
            data-testid="upload-excel-btn"
          >
            <Upload size={16} />
            Upload Excel
          </button>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="flex items-center gap-2 px-4 py-2.5 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-colors font-medium"
            data-testid="new-claim-btn"
          >
            <Plus size={18} />
            New Claim
          </button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
          message.type === 'success' ? 'bg-green-100 text-green-700' : message.type === 'info' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'
        }`}>
          {message.type === 'success' ? <CheckCircle size={18} /> : <AlertTriangle size={18} />}
          {message.text}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
          <p className="text-blue-100 text-sm">Total Claims</p>
          <p className="text-3xl font-bold">{stats.total}</p>
        </div>
        <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl p-4 text-white">
          <p className="text-yellow-100 text-sm">Pending</p>
          <p className="text-3xl font-bold">{stats.pending}</p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
          <p className="text-green-100 text-sm">Approved</p>
          <p className="text-3xl font-bold">{stats.approved}</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white">
          <p className="text-purple-100 text-sm">Total Claim Amount</p>
          <div className="space-y-0.5">
            {stats.totalAmountUSD > 0 && (
              <p className="text-2xl font-bold">${stats.totalAmountUSD.toLocaleString('en-IN', {maximumFractionDigits: 0})}</p>
            )}
            {stats.totalAmountINR > 0 && (
              <p className={`${stats.totalAmountUSD > 0 ? 'text-lg' : 'text-2xl'} font-bold`}>₹{stats.totalAmountINR.toLocaleString('en-IN', {maximumFractionDigits: 0})}</p>
            )}
            {stats.totalAmountUSD === 0 && stats.totalAmountINR === 0 && (
              <p className="text-2xl font-bold">₹0</p>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search by claim #, party, invoice..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:border-orange-400 outline-none"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:border-orange-400 outline-none"
          >
            <option value="All">All Status</option>
            {claimStatuses.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:border-orange-400 outline-none"
          >
            <option value="All">All Types</option>
            {claimTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
          <button
            onClick={() => syncGRNShortages(false)}
            disabled={syncing}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-amber-50 text-amber-700 hover:bg-amber-100 rounded-lg border border-amber-200 disabled:opacity-50"
            title="Sync GRN Shortages"
            data-testid="sync-shortages-btn"
          >
            {syncing ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Scale size={16} />
            )}
            <span className="hidden sm:inline">Sync Shortages</span>
          </button>
          <button
            onClick={fetchClaims}
            className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
            title="Refresh"
          >
            <RefreshCw size={20} />
          </button>
        </div>
      </div>

      {/* Claims Table */}
      <ListLoadError
        error={claimsLoadError}
        busy={loading}
        onRetry={() => fetchClaims()}
        label="claims"
      />
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Claim #</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">GRN/Invoice</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Party</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Type</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Shortage (MT)</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Amount</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Status</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan="9" className="px-4 py-8 text-center">
                    <Loader2 className="animate-spin mx-auto text-orange-500" size={24} />
                    <p className="text-gray-500 mt-2">Loading claims...</p>
                  </td>
                </tr>
              ) : filteredClaims.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-4 py-8 text-center text-gray-500">
                    No claims found
                  </td>
                </tr>
              ) : (
                filteredClaims.map((claim) => (
                  <tr key={claim.claimNumber} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className="font-medium text-gray-900">{claim.claimNumber}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{claim.claimDate}</td>
                    <td className="px-4 py-3">
                      <div className="text-sm">
                        <p className="font-medium text-gray-900">{claim.grnNumber || '-'}</p>
                        <p className="text-gray-500">{claim.invoiceNo || '-'}</p>
                        {claim.containerNo && <p className="text-gray-400 text-xs">{claim.containerNo}</p>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{claim.partyName}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {getTypeIcon(claim.claimType)}
                        <span className="text-sm">{claim.claimType}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-medium text-red-600">
                        {parseFloat(claim.shortageQty || 0).toFixed(3)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-medium text-gray-900">
                        {claim.currency === 'USD' ? '$' : '₹'}
                        {parseFloat(claim.claimAmount || 0).toLocaleString()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <select
                        value={claim.status}
                        onChange={(e) => handleStatusChange(claim.claimNumber, e.target.value)}
                        className={`px-2 py-1 rounded-full text-xs font-medium border-0 cursor-pointer ${getStatusColor(claim.status)}`}
                      >
                        {claimStatuses.map(status => (
                          <option key={status} value={status}>{status}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => handleDownloadPDF(claim.claimNumber)}
                          className="p-1.5 text-purple-500 hover:bg-purple-50 rounded"
                          title="Download PDF"
                        >
                          <Download size={16} />
                        </button>
                        <button
                          onClick={() => openViewModal(claim)}
                          className="p-1.5 text-blue-500 hover:bg-blue-50 rounded"
                          title="View Details"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => openEditModal(claim)}
                          className="p-1.5 text-green-500 hover:bg-green-50 rounded"
                          title="Edit"
                        >
                          <Edit2 size={16} />
                        </button>
                        {canDelete && (
                          <button
                            onClick={() => handleDelete(claim.claimNumber)}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <div className="flex items-center gap-2">
                <AlertTriangle className="text-orange-500" size={20} />
                <h2 className="font-bold text-lg">
                  {selectedClaim ? 'Edit Claim' : 'New Claim'}
                </h2>
              </div>
              <button onClick={() => { setShowModal(false); resetForm(); }} className="p-1 hover:bg-gray-100 rounded">
                <X size={20} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Link to GRN */}
              <div className="bg-blue-50 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Link to GRN (Optional)</label>
                {grnList.length > 0 ? (
                  <select
                    value={formData.grnNumber}
                    onChange={(e) => handleGRNSelect(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                  >
                    <option value="">Select GRN to auto-fill details</option>
                    {grnList.map(grn => (
                      <option key={grn.grnNumber || grn.id} value={grn.grnNumber}>
                        {grn.grnNumber} - {grn.partyName} ({grn.invoiceNo})
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={formData.grnNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, grnNumber: e.target.value }))}
                    placeholder={grnLoadError ? "Could not load GRNs - type GRN number manually" : "Loading GRNs... or type GRN number manually"}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                  />
                )}
              </div>

              {/* Basic Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Claim Date *</label>
                  <input
                    type="date"
                    value={formData.claimDate}
                    onChange={(e) => handleInputChange('claimDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-orange-400 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Claim Type *</label>
                  <select
                    value={formData.claimType}
                    onChange={(e) => handleInputChange('claimType', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-orange-400 outline-none"
                    data-testid="claim-type-select"
                  >
                    {claimTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Party Name *</label>
                  <input
                    type="text"
                    value={formData.partyName}
                    onChange={(e) => handleInputChange('partyName', e.target.value)}
                    placeholder="Supplier name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-orange-400 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Invoice No</label>
                  <input
                    type="text"
                    value={formData.invoiceNo}
                    onChange={(e) => handleInputChange('invoiceNo', e.target.value)}
                    placeholder="Invoice number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-orange-400 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Container No</label>
                  <input
                    type="text"
                    value={formData.containerNo}
                    readOnly
                    placeholder="From GRN"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed"
                    data-testid="claim-container-no"
                  />
                </div>
              </div>

              {/* Weight & Amount Details */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Claim Details</h3>

                {formData.claimType === 'Quality Issue' ? (
                  /* === QUALITY ISSUE FIELDS === */
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Attachment as per Contract (%)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.contractAttachment}
                        onChange={(e) => handleInputChange('contractAttachment', e.target.value)}
                        placeholder="e.g. 1.5"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-orange-400 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Actual Attachment (%)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.actualAttachment}
                        onChange={(e) => handleInputChange('actualAttachment', e.target.value)}
                        placeholder="e.g. 2.3"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-orange-400 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Claimable Attachment (%)</label>
                      <input
                        type="number"
                        value={formData.claimableAttachment}
                        readOnly
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-green-50 text-green-700 font-medium"
                        placeholder="Auto-calculated"
                      />
                      <p className="text-[10px] text-gray-400 mt-0.5">Actual - Contract</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Rate</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.qualityRate}
                        onChange={(e) => handleInputChange('qualityRate', e.target.value)}
                        placeholder="Rate per MT"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-orange-400 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Currency</label>
                      <select
                        value={formData.currency}
                        onChange={(e) => handleInputChange('currency', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-orange-400 outline-none"
                      >
                        <option value="USD">USD ($)</option>
                        <option value="INR">INR (Rs.)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Received Qty (MT)</label>
                      <input
                        type="number"
                        step="0.001"
                        value={formData.qualityReceivedQty}
                        onChange={(e) => handleInputChange('qualityReceivedQty', e.target.value)}
                        placeholder="Received quantity"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-orange-400 outline-none"
                      />
                    </div>
                    <div className="col-span-3 bg-blue-50 rounded-lg p-3 border border-blue-200">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Claim Amount per MT</label>
                          <div className="px-3 py-2 bg-white border border-blue-200 rounded-lg font-medium text-blue-700">
                            {formData.currency === 'INR' ? 'Rs.' : '$'}{formData.claimAmountPerMT || '0'}
                          </div>
                          <p className="text-[10px] text-gray-400 mt-0.5">Rate x Claimable %</p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Total Claim</label>
                          <div className="px-3 py-2 bg-white border border-blue-200 rounded-lg font-bold text-orange-600">
                            {formData.currency === 'INR' ? 'Rs.' : '$'}{formData.totalClaim || '0'}
                          </div>
                          <p className="text-[10px] text-gray-400 mt-0.5">Claim/MT x Qty</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* === SHORTAGE / OTHER CLAIM FIELDS === */
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Invoice Weight (MT)</label>
                      <input
                        type="number"
                        step="0.001"
                        value={formData.invoiceWeight}
                        onChange={(e) => handleInputChange('invoiceWeight', e.target.value)}
                        placeholder="As per invoice"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-orange-400 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">CFS / Actual Weight (MT)</label>
                      <input
                        type="number"
                        step="0.001"
                        value={formData.actualWeight}
                        onChange={(e) => handleInputChange('actualWeight', e.target.value)}
                        placeholder="CFS weight (or received wt)"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-orange-400 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Shortage Qty (MT)</label>
                      <input
                        type="number"
                        step="0.001"
                        value={formData.shortageQty}
                        onChange={(e) => handleInputChange('shortageQty', e.target.value)}
                        placeholder="Auto-calculated"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-orange-400 outline-none bg-gray-50"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Currency</label>
                      <select
                        value={formData.currency}
                        onChange={(e) => handleInputChange('currency', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-orange-400 outline-none"
                      >
                        <option value="USD">USD ($)</option>
                        <option value="INR">INR (Rs.)</option>
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Claim Amount</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.claimAmount}
                        onChange={(e) => handleInputChange('claimAmount', e.target.value)}
                        placeholder="Amount to claim"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-orange-400 outline-none"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-medium text-gray-700">Description</label>
                  <label
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs text-orange-600 hover:text-white hover:bg-orange-500 border border-orange-300 rounded-md cursor-pointer transition"
                    data-testid="claim-description-attach-btn"
                    title="Attach Excel / PDF file with Quality Claim details"
                  >
                    <Upload size={12} />
                    Attach File
                    <input type="file" multiple className="hidden"
                      accept=".xls,.xlsx,.csv,.pdf,.png,.jpg,.jpeg"
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        files.forEach(file => {
                          if (file.size > 5 * 1024 * 1024) { alert(`"${file.name}" exceeds 5MB limit.`); return; }
                          const reader = new FileReader();
                          reader.onload = (ev) => {
                            setFormData(prev => ({
                              ...prev,
                              documents: [...(prev.documents || []), {
                                name: file.name, type: file.type, size: file.size,
                                data: ev.target.result, uploadedAt: new Date().toISOString()
                              }]
                            }));
                          };
                          reader.readAsDataURL(file);
                        });
                        e.target.value = '';
                      }}
                    />
                  </label>
                </div>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Describe the claim details..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-orange-400 outline-none"
                />
                {formData.documents?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid="claim-description-files">
                    {formData.documents.map((doc, idx) => (
                      <div key={idx} className="flex items-center gap-1.5 px-2 py-1 bg-orange-50 border border-orange-200 rounded-md text-xs">
                        <FileText size={12} className="text-orange-500" />
                        <span className="max-w-[180px] truncate" title={doc.name}>{doc.name}</span>
                        <span className="text-gray-400">({(doc.size / 1024).toFixed(0)}KB)</span>
                        <button type="button" onClick={() => {
                          setFormData(prev => ({ ...prev, documents: prev.documents.filter((_, i) => i !== idx) }));
                        }} className="p-0.5 hover:bg-orange-100 rounded-full">
                          <X size={10} className="text-red-500" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Remarks */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Remarks</label>
                <input
                  type="text"
                  value={formData.remarks}
                  onChange={(e) => handleInputChange('remarks', e.target.value)}
                  placeholder="Any additional notes"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-orange-400 outline-none"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <Loader2 className="animate-spin" size={16} />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      {selectedClaim ? 'Update Claim' : 'Create Claim'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedClaim && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <div className="flex items-center gap-2">
                <Eye className="text-blue-500" size={20} />
                <h2 className="font-bold text-lg">Claim Details - {selectedClaim.claimNumber}</h2>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => handleDownloadPDF(selectedClaim.claimNumber)}
                  className="flex items-center gap-1 px-3 py-1.5 bg-purple-500 text-white rounded-lg hover:bg-purple-600 text-sm"
                >
                  <Download size={14} />
                  Download PDF
                </button>
                <button onClick={() => setShowViewModal(false)} className="p-1 hover:bg-gray-100 rounded">
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="p-4 space-y-4">
              {/* Status & Type */}
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedClaim.status)}`}>
                  {selectedClaim.status}
                </span>
                <div className="flex items-center gap-1 text-sm">
                  {getTypeIcon(selectedClaim.claimType)}
                  <span>{selectedClaim.claimType}</span>
                </div>
              </div>

              {/* Party & GRN Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500 text-xs">Party Name</p>
                    <p className="font-medium">{selectedClaim.partyName}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs">Claim Date</p>
                    <p className="font-medium">{selectedClaim.claimDate}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs">GRN Number</p>
                    <p className="font-medium">{selectedClaim.grnNumber || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs">Invoice No</p>
                    <p className="font-medium">{selectedClaim.invoiceNo || '-'}</p>
                  </div>
                  {selectedClaim.containerNo && (
                    <div>
                      <p className="text-gray-500 text-xs">Container No</p>
                      <p className="font-medium">{selectedClaim.containerNo}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Weight Comparison OR Quality Attachment Details */}
              {selectedClaim.claimType === 'Quality Issue' ? (() => {
                const qc = claimWithFallback(selectedClaim);
                return (
                <div className="border-t pt-4" data-testid="claim-quality-details">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Quality Claim Details</h3>
                  <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                      <div>
                        <p className="text-gray-500 text-xs">As per Contract %</p>
                        <p className="font-bold text-lg">{parseFloat(qc.contractAttachment || 0).toFixed(2)}%</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Actual Attachment %</p>
                        <p className="font-bold text-lg">{parseFloat(qc.actualAttachment || 0).toFixed(2)}%</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Claimable Attachment %</p>
                        <p className="font-bold text-lg text-red-600">{parseFloat(qc.claimableAttachment || 0).toFixed(2)}%</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Received Qty (MT)</p>
                        <p className="font-bold text-lg">{parseFloat(qc.qualityReceivedQty || 0).toFixed(3)}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-center mt-4 pt-4 border-t border-yellow-200">
                      <div>
                        <p className="text-gray-500 text-xs">Rate ({selectedClaim.currency || 'USD'} / MT)</p>
                        <p className="font-bold text-lg">{parseFloat(qc.qualityRate || 0).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Claim Amount per MT</p>
                        <p className="font-bold text-lg">{selectedClaim.currency === 'USD' ? '$' : '₹'}{parseFloat(qc.claimAmountPerMT || 0).toLocaleString(undefined, {maximumFractionDigits: 2})}</p>
                      </div>
                    </div>
                  </div>
                </div>
                );
              })() : (
                <div className="border-t pt-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Weight Details</h3>
                  <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-gray-500 text-xs">Invoice Weight</p>
                        <p className="font-bold text-lg">{parseFloat(selectedClaim.invoiceWeight || 0).toFixed(3)} MT</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">CFS Weight</p>
                        <p className="font-bold text-lg">{parseFloat(selectedClaim.actualWeight || selectedClaim.cfsWeight || 0).toFixed(3)} MT</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Shortage</p>
                        <p className="font-bold text-lg text-red-600">{parseFloat(selectedClaim.shortageQty || 0).toFixed(3)} MT</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Claim Amount */}
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <div className="flex justify-between items-center">
                  <p className="text-gray-600">Claim Amount</p>
                  <p className="text-2xl font-bold text-green-700">
                    {selectedClaim.currency === 'USD' ? '$' : '₹'}
                    {parseFloat(selectedClaim.claimAmount || 0).toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Description */}
              {selectedClaim.description && (
                <div>
                  <p className="text-gray-500 text-xs mb-1">Description</p>
                  <p className="text-gray-700 bg-gray-50 rounded-lg p-3">{selectedClaim.description}</p>
                </div>
              )}

              {/* Remarks */}
              {selectedClaim.remarks && (
                <div>
                  <p className="text-gray-500 text-xs mb-1">Remarks</p>
                  <p className="text-gray-700">{selectedClaim.remarks}</p>
                </div>
              )}

              {/* Supporting Documents */}
              {selectedClaim.documents?.length > 0 && (
                <div>
                  <p className="text-gray-500 text-xs mb-2">Supporting Documents</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedClaim.documents.map((doc, idx) => (
                      <a key={idx} href={doc.data} download={doc.name}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-xs hover:bg-blue-100 transition">
                        <FileText size={12} className="text-blue-500" />
                        <span>{doc.name}</span>
                        <Download size={10} className="text-blue-400" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Created Info */}
              <div className="text-xs text-gray-500 pt-2 border-t">
                Created by {selectedClaim.createdBy || 'Admin'} on {new Date(selectedClaim.createdAt).toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload Excel Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <div className="flex items-center gap-2">
                <Upload className="text-teal-500" size={20} />
                <h2 className="font-bold text-lg" data-testid="upload-modal-title">Upload Claims Excel</h2>
              </div>
              <button onClick={() => { setShowUploadModal(false); setUploadResult(null); }} className="p-1 hover:bg-gray-100 rounded">
                <X size={20} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {!uploadResult ? (
                <>
                  <div className="border-2 border-dashed border-teal-300 rounded-xl p-8 text-center bg-teal-50/50">
                    <Upload className="mx-auto text-teal-400 mb-3" size={40} />
                    <p className="text-gray-700 font-medium mb-1">Upload Excel file with claim details</p>
                    <p className="text-xs text-gray-500 mb-4">
                      Free-format supported. System will auto-detect columns like Party, Invoice, Weight, Shortage, Amount, etc.
                    </p>
                    <label className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-500 text-white rounded-lg hover:bg-teal-600 cursor-pointer transition font-medium text-sm">
                      {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                      {uploading ? 'Processing...' : 'Choose File'}
                      <input
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleExcelUpload}
                        disabled={uploading}
                        className="hidden"
                        data-testid="excel-file-input"
                      />
                    </label>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600">
                    <p className="font-semibold mb-1">How it works:</p>
                    <ul className="list-disc pl-4 space-y-0.5">
                      <li>Upload any Excel with claim data (free format)</li>
                      <li>System detects columns: Party, Invoice, GRN, Invoice Wt, CFS Wt, Shortage, Amount, etc.</li>
                      <li>Claims are created in the system automatically</li>
                      <li>A PDF report is generated with the Excel data for download</li>
                    </ul>
                  </div>
                </>
              ) : uploadResult.error ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-red-700 font-medium mb-2">
                    <AlertTriangle size={18} />
                    Upload Failed
                  </div>
                  <p className="text-sm text-red-600">{uploadResult.error}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-green-700 font-medium mb-2">
                      <CheckCircle size={18} />
                      Upload Successful
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="bg-white rounded p-2 text-center">
                        <p className="text-2xl font-bold text-green-600">{uploadResult.created}</p>
                        <p className="text-xs text-gray-500">Claims Created</p>
                      </div>
                      {uploadResult.errors > 0 && (
                        <div className="bg-white rounded p-2 text-center">
                          <p className="text-2xl font-bold text-red-500">{uploadResult.errors}</p>
                          <p className="text-xs text-gray-500">Errors</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Detected columns info */}
                  {uploadResult.detectedColumns && Object.keys(uploadResult.detectedColumns).length > 0 && (
                    <div className="bg-blue-50 rounded-lg p-3">
                      <p className="text-xs font-semibold text-blue-700 mb-1">Detected Columns</p>
                      <div className="flex flex-wrap gap-1">
                        {Object.keys(uploadResult.detectedColumns).map(col => (
                          <span key={col} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">{col}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Created claims preview */}
                  {uploadResult.claims && uploadResult.claims.length > 0 && (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-600">
                        Created Claims Preview
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="px-2 py-1 text-left">Claim #</th>
                              <th className="px-2 py-1 text-left">Party</th>
                              <th className="px-2 py-1 text-right">Shortage</th>
                              <th className="px-2 py-1 text-right">Amount</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {uploadResult.claims.map((c, i) => (
                              <tr key={i} className="hover:bg-gray-50">
                                <td className="px-2 py-1 font-medium">{c.claimNumber}</td>
                                <td className="px-2 py-1">{c.partyName || '-'}</td>
                                <td className="px-2 py-1 text-right text-red-600">{(c.shortageQty || 0).toFixed(3)}</td>
                                <td className="px-2 py-1 text-right">{(c.claimAmount || 0).toLocaleString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Download PDF button */}
                  {uploadResult.pdfId && (
                    <button
                      onClick={() => handleDownloadUploadPDF(uploadResult.pdfId)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition font-medium text-sm"
                      data-testid="download-upload-pdf-btn"
                    >
                      <Download size={16} />
                      Download Claims PDF
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-200 flex justify-end gap-3 sticky bottom-0 bg-white">
              <button
                onClick={() => { setShowUploadModal(false); setUploadResult(null); }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Claims;
